import { useQuery } from "@tanstack/react-query";
import { useContext } from "react";
import { orderBy } from "lodash";
import { defaultApiUrl, get } from "../api/util";
import { Hotel } from "../interfaces/schema";
import { UserQueryContext } from "../context/UserContext";

export function useKodeListe(kodeliste: KodeListeType) {
  const { isPending, data, error } = useQuery({
    queryKey: ["kodelister"],
    queryFn: async function () {
      return get<KodeLister>({ query: "kodelister" });
    },
    staleTime: Infinity,
  });
  return {
    getKodelisteError: error,
    getKodelisteIsLoading: isPending,
    kodeliste: data ? data[kodeliste] : undefined,
  };
}

const kodeListeDetailEndpoints: Partial<Record<KodeListeType, string>> = {
  Sanktionstype: "kodelister/sanktionstype",
};

export const isKodeListeDeaktiveret = (item: KodeListe): boolean => {
  const record = item as KodeListe & { Deaktiveret?: boolean };
  return Boolean(record.deaktiveret ?? record.Deaktiveret);
};

const mapSanktionstyperToKodeListe = (
  items: IKodelisteSanktionstyper[],
): KodeListe[] =>
  items.map((item) => ({
    type: "liste",
    nr: item.nr,
    navn: item.navn,
    beskrivelse: null,
    deaktiveret:
      item.deaktiveret ??
      (item as IKodelisteSanktionstyper & { Deaktiveret?: boolean })
        .Deaktiveret ??
      false,
  }));

export function useSelectableKodeListe(
  kodeListeType: KodeListeType,
  options?: { excludeDeaktiveret?: boolean },
) {
  const detailEndpoint =
    options?.excludeDeaktiveret && kodeListeType === "Sanktionstype"
      ? kodeListeDetailEndpoints[kodeListeType]
      : undefined;

  const {
    getKodelisteError: standardError,
    getKodelisteIsLoading: standardIsLoading,
    kodeliste: standardKodeliste,
  } = useKodeListe(kodeListeType);

  const {
    isPending: detailIsLoading,
    data: detailData,
    error: detailError,
  } = useQuery({
    queryKey: [detailEndpoint],
    queryFn: async function () {
      return get<IKodelisteSanktionstyper[]>({ query: detailEndpoint! });
    },
    enabled: Boolean(detailEndpoint),
    staleTime: Infinity,
  });

  if (detailEndpoint) {
    const kodeliste = detailData
      ? mapSanktionstyperToKodeListe(detailData).filter(
          (item) => !isKodeListeDeaktiveret(item),
        )
      : undefined;

    return {
      getKodelisteError: detailError,
      getKodelisteIsLoading: detailIsLoading,
      kodeliste,
    };
  }

  let kodeliste = standardKodeliste;
  if (options?.excludeDeaktiveret) {
    kodeliste = standardKodeliste?.filter(
      (item) => !isKodeListeDeaktiveret(item),
    );
  }

  return {
    getKodelisteError: standardError,
    getKodelisteIsLoading: standardIsLoading,
    kodeliste,
  };
}

export function useGenericQuery<Data>(
  query: string,
  options?: {
    orderBy?: string;
  },
): {
  data: Data[] | undefined;
  isPending: boolean;
  error: unknown;
} {
  const { isPending, data, error } = useQuery({
    queryKey: [query],
    queryFn: async function () {
      const data = await get<Data[]>({ query });
      return options?.orderBy ? orderBy(data, options.orderBy) : orderBy(data);
    },
  });

  return { data, isPending, error };
}

export function useHardcodedQuery<Data>(data: Data[] | undefined): {
  data: Data[] | undefined;
  isPending: boolean;
  error: unknown;
} {
  const isPending = false;
  const error = false;
  return { data, isPending, error };
}

export interface Report {
  title: string;
  mediaType: string;
  uiPath:
    | "aika/web/person"
    | "aika/web/firma"
    | "aika/web/kursus"
    | "aika/web/personliste"
    | "aika/web/retssagsproeve"
    | "aika/web/system";
  accepts: string;
  id: string;
  template: string;
  url: string;
  requiresBestaaetConfirmation?: boolean;
  /** Den permission, backend kræver for at danne rapporten (fx "CprRead"). */
  policyName?: string | null;
}

/**
 * ADV-349: backend afviser at danne en rapport, hvis brugeren mangler dens policy; skjul den
 * derfor også i menuerne (fx eksamensbeviser, der kræver CprRead). Rapporter uden policy vises
 * som hidtil.
 */
export function userCanCreateReport(
  report: Pick<Report, "policyName">,
  permissions: readonly string[],
): boolean {
  if (!report.policyName) return true;
  const wanted = report.policyName.toLowerCase();
  return permissions.some((p) => p.toLowerCase() === wanted);
}

export function useReports() {
  const { user } = useContext(UserQueryContext);
  const {
    isPending: getReportsIsLoading,
    data: reports,
    error: getReportsError,
  } = useQuery({
    queryKey: ["reports"],
    queryFn: async function () {
      const reports = await get<Report[]>({ query: "reports" });
      return reports.map((r) => {
        return {
          ...r,
          url: `${defaultApiUrl}reports/${r.id}`,
        };
      });
    },
  });

  return {
    getReportsIsLoading,
    reports: reports?.filter((r) =>
      userCanCreateReport(r, user.permissions ?? []),
    ),
    getReportsError,
  };
}

export function useUdenlandskeAdvokatsamfund() {
  const { isPending, data, error } = useQuery({
    queryKey: ["udenlandskdvokatsamfund"],
    queryFn: async function () {
      return get<IUdenlandskAdvokatsamfund[]>({
        query: "udenlandskadvokatsamfund",
      });
    },
  });

  return {
    getUdenlandskAdvokatsamfundError: error,
    getUdenlandskAdvokatsamfundIsLoading: isPending,
    udenlandskeAdvokatsamfund: data,
  };
}

export function useHotels() {
  const { isPending, data, error } = useQuery({
    queryKey: ["hotel"],
    queryFn: async function () {
      return get<Hotel[]>({
        query: "hotel",
      });
    },
  });
  const getHotelById = (id: string) => {
    return data?.find((h) => h.id === id);
  };

  return {
    getHotelsError: error,
    getHotelsLoading: isPending,
    hotels: data ? orderBy(data, "navn") : undefined,
    getHotelById,
  };
}
